import type { AuditEvent } from '@ralph/contracts'
import clsx from 'clsx'
import { AlertTriangle, ArrowRight, Ban, Check, CornerDownLeft, ScrollText, ShieldAlert } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import { SharingQueue } from '@/components/sharing/SharingQueue.tsx'
import { Badge, EmptyState, Panel, Spinner } from '@/components/ui/primitives.tsx'
import { useApprovals, useAudit, useSession } from '@/hooks/use-api.ts'
import { useSharingQueue } from '@/hooks/use-sharing.ts'
import { describeApiError, useI18n } from '@/i18n/index.ts'

/**
 * Governance: what is waiting on a human, and what the platform recorded.
 *
 * The audit stream is immutable and separate from operational logging on purpose — a
 * reviewer reconstructing "who approved sending that, and to whom" needs a record that
 * cannot have been rotated away with last week's logs.
 *
 * There is deliberately **no approve or reject button for a tool call on this page**. Resolving
 * an approval means echoing back the digest of the exact arguments the approver was shown, and
 * that digest is shown in the conversation — so every row here leads back to where the request is
 * legible and nothing more.
 *
 * **Sharing requests** are the exception, and for the same reason: their card IS the legible
 * form of the request (PRD-26 D-29). It shows everything the decision is about, and the decision
 * carries the digest of what the card showed (D-49). Administrators only.
 *
 * The view and the selected request live in the URL (`?view=sharing&request=…`) so a
 * notification can land on the request it is about.
 */
type View = 'approvals' | 'sharing' | 'audit'

export const GovernancePage = () => {
  // Bound inside the body, never at module scope: a capture taken at import freezes the
  // locale and then renders the previous language with no error at all (PRD-27 F-3).
  // `fmt` whole as well as the three bound helpers: the pure strings modules take a
  // `Pick<I18n, ...>` pack rather than individual formatters, so a partial destructure cannot
  // satisfy them (PRD-27 LOC-012).
  const fmt = useI18n()
  const t = fmt.t.governance
  const { formatAgo, formatCount, formatUntil } = fmt
  const session = useSession()
  const approvals = useApprovals()
  const [params, setParams] = useSearchParams()

  const roles = session.data?.user?.roles ?? []
  const isAdmin = roles.includes('admin')
  const canAudit = roles.includes('auditor') || roles.includes('operator') || isAdmin
  const requested = params.get('view')
  const tab: View =
    requested === 'sharing' && isAdmin ? 'sharing' : requested === 'audit' && canAudit ? 'audit' : 'approvals'
  const setTab = (next: View) => setParams(next === 'approvals' ? {} : { view: next }, { replace: true })
  const audit = useAudit(canAudit && tab === 'audit')
  const sharing = useSharingQueue('pending', isAdmin)
  const waitingShares = sharing.data?.items.length ?? 0

  const pending = approvals.data?.items ?? []

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-[992px] px-4 pb-[34px] pt-1 sm:px-8 lg:px-10">
        <header className="flex flex-wrap items-end gap-x-4 gap-y-3 pb-5 pt-4">
          <div>
            <h1 className="text-[30px] sm:text-[38px]">{t.title}</h1>
            <p className="mt-[7px] max-w-[440px] text-pretty text-[13.5px] leading-[1.5] text-fg-muted">{t.lead}</p>
          </div>

          {/*
            Two segmented pills rather than a `role="tablist"`: a real tablist owes the reader
            arrow-key navigation, a roving tabindex and a labelled tabpanel, and a half-built
            one is worse than the plain buttons this page has always had. `aria-pressed` says
            which view is showing without promising keyboard behaviour that is not here.
          */}
          <div className="flex flex-wrap items-center gap-1.5 sm:ml-auto">
            {((isAdmin ? ['approvals', 'sharing', 'audit'] : ['approvals', 'audit']) as View[]).map((value) => {
              const active = tab === value
              const waiting = value === 'approvals' ? pending.length : value === 'sharing' ? waitingShares : 0
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setTab(value)}
                  disabled={value === 'audit' && !canAudit}
                  aria-pressed={active}
                  className={clsx(
                    'inline-flex h-[30px] items-center gap-1.5 rounded-full border px-3 text-[12px] tracking-[-0.01em]',
                    'transition-[color,background-color,border-color,box-shadow] duration-200',
                    'disabled:pointer-events-none disabled:opacity-40',
                    active
                      ? 'border-rule-strong bg-card-solid font-semibold text-fg shadow-glass'
                      : 'border-rule bg-card text-fg-muted hover:border-rule-strong hover:text-fg'
                  )}
                >
                  {value === 'approvals' ? t.approvalsTab : value === 'sharing' ? fmt.t.sharing.view : t.auditTrail}
                  {waiting > 0 && (
                    <>
                      <span
                        aria-hidden
                        className={clsx('readout text-[9px]', value === 'approvals' ? 'text-alert' : 'text-primary')}
                      >
                        {formatCount(waiting)}
                      </span>
                      {/* "Sharing requests, 3 waiting" to a screen reader, not "Sharing requests3". */}
                      <span className="sr-only">, {fmt.t.sharing.waiting(fmt, waiting)}</span>
                    </>
                  )}
                </button>
              )
            })}
          </div>
        </header>

        {tab === 'sharing' ? (
          <SharingQueue selectedId={params.get('request')} />
        ) : tab === 'approvals' ? (
          pending.length === 0 ? (
            <Panel className="px-4">
              <EmptyState title={t.empty.title} body={t.empty.body} />
            </Panel>
          ) : (
            <Panel className="overflow-hidden">
              <div className="flex items-center gap-2 border-b border-rule px-4 py-3">
                <ShieldAlert size={12} className="shrink-0 text-fg-muted" aria-hidden />
                <h2 className="eyebrow">{t.queueTitle}</h2>
                <span className="readout ml-auto hidden items-center gap-1.5 text-fg-muted sm:flex">
                  <CornerDownLeft size={10} aria-hidden />
                  {t.queueHint}
                </span>
              </div>
              <ul className="divide-y divide-rule">
                {pending.map((approval, index) => (
                  <li
                    key={approval.id}
                    className="rise"
                    // Capped, so a full queue is not still arriving seconds after it loaded.
                    style={{ animationDelay: `${Math.min(index, 8) * 45}ms` }}
                  >
                    <div className="flex flex-wrap items-start gap-x-3 gap-y-2.5 px-3 py-3.5 transition-colors duration-200 hover:bg-card-solid sm:px-4">
                      <span
                        aria-hidden
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-tile bg-alert-soft text-alert"
                      >
                        <ShieldAlert size={14} />
                      </span>

                      <div className="min-w-0 flex-1 basis-[15rem]">
                        <p className="text-[13.5px] font-semibold tracking-[-0.014em]">{approval.displayName}</p>
                        <p className="mt-1 text-pretty text-[12.5px] leading-relaxed text-fg-muted">
                          {approval.rationale}
                        </p>
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          <Badge tone="alert">{t.risk[approval.risk]}</Badge>
                          {!approval.reversible && (
                            <Badge tone="alert">
                              <Ban size={10} className="shrink-0" aria-hidden />
                              {t.irreversible}
                            </Badge>
                          )}
                          {/*
                            `formatUntil`, not `formatRelative`: the latter clamps every
                            future timestamp to "just now", which made every unexpired
                            approval in this queue read as if it were seconds from lapsing.
                          */}
                          <span className="readout text-fg-subtle">
                            {t.requestedAndExpires(formatAgo(approval.requestedAt), formatUntil(approval.expiresAt))}
                          </span>
                        </div>
                      </div>

                      <Link
                        to={`/c/${approval.conversationId}`}
                        className="ml-auto inline-flex h-[30px] shrink-0 items-center gap-1.5 rounded-full border border-rule bg-card px-3 text-[12px] font-semibold tracking-[-0.014em] text-fg-muted transition-[color,background-color,border-color,box-shadow] duration-200 hover:border-rule-strong hover:bg-card-solid hover:text-fg"
                      >
                        {t.reviewInConversation}
                        <ArrowRight size={12} aria-hidden />
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            </Panel>
          )
        ) : (
          <AuditPanel
            events={audit.data?.items ?? []}
            loading={audit.isLoading}
            failed={audit.isError}
            failureDetail={audit.error ? describeApiError(fmt.t, audit.error) : null}
          />
        )}
      </div>
    </div>
  )
}

/**
 * The record, in four states.
 *
 * The fourth one is why this is not a plain table: a failed fetch used to fall through to the
 * "No audit events" empty state, so an outage and a clean record looked identical — the worst
 * possible confusion on the one surface whose job is to say what happened.
 */
const AuditPanel = ({
  events,
  loading,
  failed,
  failureDetail
}: {
  /**
   * The contract row itself rather than a structural copy of it. The copy typed `outcome` as
   * `string`, which is what let the column print the wire value verbatim; taking `AuditEvent`
   * makes the label map below exhaustive against the enum the server actually sends.
   */
  events: AuditEvent[]
  loading: boolean
  failed: boolean
  /**
   * The support line under the two sentences above: already rendered, already in the reader's
   * language. It arrives as a string rather than as the error itself because this panel says
   * what it is told and does not decide what a failure means (PRD-27 LOC-033).
   */
  failureDetail: string | null
}) => {
  const fmt = useI18n()
  const t = fmt.t.governance
  const { formatAgo } = fmt

  if (loading) {
    return (
      <Panel role="status" className="flex items-center gap-2 px-4 py-6 text-[13px] text-fg-subtle">
        <Spinner />
        {t.auditLoading}
      </Panel>
    )
  }

  if (failed) {
    return (
      <Panel className="px-4 py-10">
        <div role="alert" className="mx-auto flex max-w-md flex-col items-center gap-3 text-center">
          <span aria-hidden className="flex h-9 w-9 items-center justify-center rounded-full bg-alert-soft text-alert">
            <AlertTriangle size={16} />
          </span>
          <h3 className="text-[15px]">{t.auditFailedTitle}</h3>
          <p className="text-pretty text-[13.5px] leading-relaxed text-fg-muted">{t.auditFailedBody}</p>
          {failureDetail && <p className="readout text-fg-subtle">{failureDetail}</p>}
        </div>
      </Panel>
    )
  }

  if (events.length === 0) {
    return (
      <Panel className="px-4">
        <EmptyState title={t.auditEmpty.title} body={t.auditEmpty.body} />
      </Panel>
    )
  }

  return (
    <Panel className="overflow-hidden">
      <div className="flex items-center gap-2 border-b border-rule px-4 py-3">
        <ScrollText size={12} className="shrink-0 text-fg-muted" aria-hidden />
        <h2 className="eyebrow">{t.auditTrail}</h2>
        {/* The endpoint asks for the last 100, so this counts what is on the screen and says
            so — a bare total would claim the record ends here. */}
        <span className="readout ml-auto text-fg-muted">{t.shown(fmt, events.length)}</span>
      </div>

      {/*
        Five columns of machine record. Below `md` this scrolls sideways inside its own
        container rather than collapsing into label/value cards: an auditor scanning a hundred
        events reads down a column, and five hundred stacked cards is not that page.
      */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[38rem] text-[13px]">
          <thead>
            <tr className="border-b border-rule text-left">
              <th className="eyebrow whitespace-nowrap px-4 py-2.5">{t.columns.when}</th>
              <th className="eyebrow whitespace-nowrap px-4 py-2.5">{t.columns.actor}</th>
              <th className="eyebrow whitespace-nowrap px-4 py-2.5">{t.columns.action}</th>
              <th className="eyebrow whitespace-nowrap px-4 py-2.5">{t.columns.resource}</th>
              <th className="eyebrow whitespace-nowrap px-4 py-2.5">{t.columns.outcome}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            {events.map((event) => (
              <tr key={event.id} className="align-top transition-colors duration-200 hover:bg-card-solid">
                <td className="readout whitespace-nowrap px-4 py-2.5 text-fg-subtle">{formatAgo(event.at)}</td>
                <td className="px-4 py-2.5">{event.actorLabel}</td>
                <td className="readout px-4 py-2.5">{event.action}</td>
                <td className="readout px-4 py-2.5 text-fg-muted">{event.resourceType}</td>
                <td className="px-4 py-2.5">
                  {/* Icon and word together: the outcome column is the one place on this page
                      where a reader skims for a single value, and colour alone cannot carry
                      it (AGM-124). */}
                  <Badge tone={event.outcome === 'allow' ? 'grounded' : event.outcome === 'deny' ? 'alert' : 'primary'}>
                    {event.outcome === 'allow' ? (
                      <Check size={10} aria-hidden />
                    ) : event.outcome === 'deny' ? (
                      <Ban size={10} aria-hidden />
                    ) : (
                      <AlertTriangle size={10} aria-hidden />
                    )}
                    {t.outcome[event.outcome]}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}
